/**
 * @desc 侧边菜单组件
 * @time 2023-07-31 15:45:43
 * @user simxin
 */

import { useEffect, useState, Fragment, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Menu } from '@tencent/tea-component';
import { HotBeta } from '@tencent/tpm-trtc-console';
import { LinkSkipIcon } from '@tencent/tpm-audio-video-react-icons';
import { LayoutSider } from '@src/component/layout/full';
import { usePortalStore } from '@src/store';
import { isSimpleAppMode } from '@src/utils/tools';
import { parseChatMenu } from '@src/utils/patch/chat';
import {
  parseApp,
  parseRouterParam,
  parseAppStrWithMenu,
} from '@src/utils/parser';
import { useDelayedRedirect } from '../link/hooks/useDelayedRedirect';
import RedirectModalWrapper from '../link/redirect-modal';
import { reportLeftMenuClick } from '@src/utils/report/index';
import { MenuTaskCard } from './menu-task-card';

export interface IMenuItemProps extends Application.Menu {
  selected: any;
  onClick: any;
  disabled?: boolean;
}

export interface IMenuItemWrap extends Application.Menu {
  getIsSelectedMenu: (m: string, s?: string) => boolean; // 判断菜单是否被选中
  useapp?: string; // 用于menu的跳转
  openedMenuKey?: number | null; // 当前展开的菜单key
  onOpenedChange?: (key: number | null) => void; // 菜单展开变化回调
  fontKey: number;
  disabledPaths?: string[];
}

/**
 * @desc 自定义配置较个性化，先硬编码
 */
// 菜单分隔符
const aiPath = 'ai-conversation';

interface LayoutMenuProps {
  disabledPaths?: string[];
}

export const LayoutMenu = ({ disabledPaths = [] }: LayoutMenuProps) => {
  const { app, menu, submenu } = useParams();
  const [title, setTitle] = useState<string>('');
  const [icon, setIcon] = useState<string>('');
  const [back, setBack] = useState<string>('');
  const [menus, setMenus] = useState<Application.Menu[]>([]);
  const [usemenu, setUsemenu] = useState(menu);
  const [usesubmenu, setUsesubmenu] = useState(submenu);
  const [useapp, setUseapp] = useState(app);
  const [openedMenuKey, setOpenedMenuKey] = useState<number | null>(null);

  useEffect(() => {
    if (isSimpleAppMode()) {
      parseAppStrWithMenu(menu!).then(ret => {
        setUseapp(ret);
      });
    }
  }, [menu]);

  useEffect(() => {
    if (useapp) {
      parseApp(useapp!).then(ret => {
        setTitle(ret.desc);
        setIcon(ret.icon!);
        setBack(ret.goback!);
        setMenus(ret.menus);
      });
    }
  }, [useapp]);

  useEffect(() => {
    setUsemenu(menu);
    setUsesubmenu(submenu);
  }, [menu, submenu]);

  /**
   * @desc tea-history 变动后，通知到侧边菜单
   */
  useEffect(() => {
    const reRouterEvent = (e: any) => {
      const { router } = e.detail;
      parseRouterParam(router).then(({ app, menu, submenu }) => {
        setUseapp(app);
        setUsemenu(menu);
        setUsesubmenu(submenu);
      });
    };
    document.body.addEventListener<any>('router:re', reRouterEvent);
    return () => {
      document.body.removeEventListener<any>('router:re', reRouterEvent, false);
    };
  }, []);

  /**
   * @desc 菜单高亮匹配
   * @param m：菜单一级路由
   * @param s：菜单二级路由
   * @var usemenu：链接一级路由
   * @var usesubmenu：链接二级路由
   */
  const getIsSelectedMenu = (m: string, s?: string) => {
    // 匹配到菜单一二级
    const match = menus.some(
      u => u.path === usemenu || u.submenus!.some(v => v.path === usemenu),
    );
    // 匹配不到菜单
    if (!match && !s) {
      const base = menus[0];
      return base.submenus?.length
        ? m === base.path && s === base.submenus[0].path
        : m === base.path;
    }
    // 菜单有二级，链接只有一级
    if (s && !usesubmenu) {
      return s === usemenu;
    }
    // 菜单只有一级，链接有二级
    if (!s && usesubmenu) {
      return m === usemenu;
    }
    // 链接有一级和二级
    if (usemenu && usesubmenu) {
      // 能匹配到菜单一二级
      const exact = m === usemenu && s === usesubmenu;
      // 菜单一级为 /，二级匹配链接一级
      const feat = m === '/' && s === usemenu;
      return exact || feat;
    }
    // 链接有一级且菜单一级为 ‘/’
    if (usemenu && m === '/') {
      return !menus.some(
        u => u.path === usemenu || u.submenus!.some(v => v.path === usemenu),
      );
    }
    // 链接有一级，没二级
    if (usemenu && !usesubmenu) {
      return m === usemenu;
    }
    // 默认高亮第一个菜单
    const base = menus[0];
    return base.submenus?.length
      ? m === base.path && s === base.submenus[0].path
      : m === base.path;
  };

  return (
    <MenuWrap
      icon={icon}
      back={back}
      title={title}
      foldable
      defaultFold={false}
      className="is-v2 hide-line is-menu-append-card"
      CustomRender={<MenuTaskCard />}
    >
      {menus?.length
        ? menus.map((m, index) => (
            <Fragment key={`fragment-${title}-${index}`}>
              <MenuItemWrap
                {...m}
                fontKey={m.key}
                useapp={useapp}
                submenus={m.submenus || []}
                getIsSelectedMenu={getIsSelectedMenu}
                openedMenuKey={openedMenuKey}
                onOpenedChange={setOpenedMenuKey}
                disabledPaths={disabledPaths}
              />
            </Fragment>
          ))
        : null}
    </MenuWrap>
  );
};

const MenuItemWrap = (props: IMenuItemWrap) => {
  const {
    submenus,
    title,
    icon,
    path,
    targetUrl,
    defaultOpen,
    tag,
    getIsSelectedMenu,
    useapp,
    openedMenuKey,
    onOpenedChange,
    fontKey,
    disabledPaths = [],
  } = props;
  const navigate = useNavigate();
  const {
    redirectModalVisible,
    closeRedirectModal,
    redirectWithModal,
    redirectModalProps,
  } = useDelayedRedirect();

  const navMenuPath = (m: string, s?: string, target?: string) => {
    if (target) {
      if (target.includes(window.QCLOUD_ROOT_HOST)) {
        redirectWithModal(target);
      } else {
        window.open(target, '_blank');
      }

      return;
    }
    if (isSimpleAppMode()) {
      navigate(`${m === '/' ? (s ? '' : '/') : `/${m}`}${s ? `/${s}` : ''}`);
    } else {
      navigate(`/${useapp}${m === '/' ? '' : `/${m}`}${s ? `/${s}` : ''}`);
    }
  };

  const isAllSubmenusDisabled = useMemo<boolean>(
    () =>
      !!submenus?.length && submenus.every(s => disabledPaths.includes(s.path)),
    [submenus, disabledPaths],
  );

  const defaultOpened = useMemo<boolean>(
    () =>
      !!defaultOpen || !!submenus?.some?.(s => getIsSelectedMenu(path, s.path)),
    [defaultOpen, submenus],
  );

  const handleOpenedChange = (opened: boolean) => {
    if (opened) {
      onOpenedChange?.(fontKey);
    } else {
      onOpenedChange?.(null);
    }
  };

  return (
    <>
      {submenus?.length ? (
        <Menu.SubMenu
          className={`micro-layout-menu ${
            isAllSubmenusDisabled ? 'is-disabled' : ''
          }`}
          title={tag ? <HotBeta text={title} tag={tag} /> : title}
          icon={icon}
          opened={
            openedMenuKey === fontKey ||
            (openedMenuKey === null && defaultOpened)
          }
          onOpenedChange={handleOpenedChange}
        >
          {submenus.map((s, sIndex) => (
            <Fragment key={`fragment-${title}-${s.title}-${sIndex}`}>
              <MenuItem
                {...s}
                selected={getIsSelectedMenu(path, s.path)}
                disabled={disabledPaths.includes(s.path)}
                onClick={() => {
                  navMenuPath(path, s.path, s.targetUrl);
                }}
              />
            </Fragment>
          ))}
        </Menu.SubMenu>
      ) : (
        <Fragment>
          {
            <MenuItem
              {...props}
              selected={getIsSelectedMenu(path)}
              disabled={disabledPaths.includes(path)}
              onClick={() => {
                navMenuPath(path, '', targetUrl);
              }}
            />
          }
        </Fragment>
      )}
      <RedirectModalWrapper
        {...redirectModalProps}
        visible={redirectModalVisible}
        close={closeRedirectModal}
      />
    </>
  );
};

const MenuItem = ({
  title,
  icon,
  tag,
  targetUrl,
  group,
  selected,
  onClick,
  path,
  disabled,
}: IMenuItemProps) => {
  const handleMenuItemClick = () => {
    if (disabled) return;
    reportLeftMenuClick(path);
    onClick();
  };
  return group === 'true' ? (
    <Menu.Group title={title}></Menu.Group>
  ) : (
    <Menu.Item
      className={`micro-layout-menu ${
        path === aiPath ? 'trtc-menu-item-light' : ''
      } ${disabled ? 'is-disabled' : ''}`}
      icon={icon}
      title={
        <>
          {tag ? <HotBeta text={title} tag={tag} /> : title}
          {targetUrl ? <LinkSkipIcon size={12} /> : ''}
        </>
      }
      selected={selected}
      onClick={handleMenuItemClick}
    />
  );
};

const MenuWrap = (props: IProps) => {
  const navigate = useNavigate();
  const {
    icon,
    back,
    title,
    foldable,
    defaultFold,
    className,
    CustomRender,
    menuTitle,
  } = props;
  // menu 自动收起的宽度 1480，（body最小宽是1400，再小会出现滚动条）
  const folded = () => window.innerWidth <= 1480;
  const [fold, setFold] = useState(folded);
  /**
   * @desc 侧边菜单自动折叠
   */
  useEffect(() => {
    const getSize = () => {
      setFold(folded);
    };
    window.addEventListener('resize', getSize);
    return () => {
      window.removeEventListener('resize', getSize);
    };
  }, []);

  /**
   * @desc 侧边菜单chat折叠
   */
  useEffect(() => {
    if (!defaultFold || foldable === fold) return;
    setFold(defaultFold || folded());
  }, [defaultFold]);

  return (
    <Menu
      className={`${className}`}
      icon={icon}
      collapsable={foldable}
      collapsed={foldable && fold}
      onCollapsedChange={able => {
        setFold(able);
      }}
      title={menuTitle || null}
      back={
        back
          ? {
              title: (
                <span
                  style={{
                    display: 'inline-block',
                    width: '100%',
                    height: '100%',
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate(back)}
                >
                  {title}
                </span>
              ),
              onClick: () => navigate(back),
            }
          : undefined
      }
      customRender={CustomRender ? () => <>{CustomRender}</> : undefined}
    >
      {props.children}
    </Menu>
  );
};
